import { ProgressStatus } from "../general/homeAssignment";
import { AssignmentStatus } from "../userAssignment/types";

export enum AssignmentType {
  COURSE = "course",
  POLICY = "policy",
  SURVEY = "survey",
}

export enum PackageType {
  Scorm12 = "Scorm12",
  Scorm2004 = "Scorm2004",
  GomoScorm12 = "GomoScorm12",
  GomoScorm2004 = "GomoScorm2004",
  xApi = "xApi",
  PdfUpload = "PdfUpload",
  VideoUpload = "VideoUpload",
}

export enum Status {
  ACTIVE = "Active",
  INACTIVE = "Inactive",
  DRAFT = "Draft",
}

export interface GetAssignmentParams {
  assignmentType?: AssignmentType;
  packageType?: PackageType;
  status?: Status;
  owner?: string;
  code?: string;
  title?: string;
  limit?: number;
  offset?: number;
  orderBy?: string;
  sort?: string;
  search?: string;
  version?: string;
  showLastPage?: boolean;
  short?: boolean;
}

export interface GetDdlAssignmentsParams {
  assignmentId?: string;
}

export interface DdlAssignment {
  AssignmentId: number;
  AssignmentUid: string;
  PortalAssignmentUid: string;
  title: string;
}

export interface AssignmentResponse {
  count: number;
  previousPage: string;
  nextPage: string;
  rows: Assignment[];
}

export interface PortalAssignmentShort {
  portalAssignmentUid: string;
  title: string;
  packageType: PackageType;
  code: string;
}

export interface GetAllAssignmentsShortPaginatedResult {
  count: number;
  previousPage: string;
  nextPage: string;
  rows: PortalAssignmentShort[];
}

export interface Assignment {
  portalUid: string;
  assignmentUid: string;
  assignmentVersionUid: string;
  title: string;
  titleShort: string;
  assignmentType: AssignmentType;
  packageType: PackageType;
  SKU: string;
  code: string;
  status: Status;
  owner: string;
  ownerUid: string;
  assignedUsers: number;
  isAssigned: boolean;
  isAddedToPortal: boolean;
  version: string;
  note: string;
}

export interface GetAssignmentResult {
  portalUid: string;
  assignmentUid: string;
  title: string;
  titleShort: string;
  assignmentType: AssignmentType;
  packageType: PackageType;
  SKU: string;
  assignmentDependency: AssignmentDependency[];
  code: string;
  tags: string[];
  status: Status;
  thumbnail: string;
  isCustom: boolean;
  thumbnailUrlVisible: boolean;
  isAvailableToSubportals: boolean;
  maxAttempts?: number;
  assignmentVersions: AssignmentVersion[];
  pageByPage?: boolean;
  acknowledgement?: boolean;
  mandatory?: boolean;
  titleTranslation: AssignmentTitleTranslation[];
  titleShortTranslation: AssignmentTitleShortTranslation[];
}

export interface AssignmentVersion {
  assignmentVersionUid?: string;
  /** Legacy backend response casing; read as a fallback until the backend
   * renames its response fields to camelCase. */
  AssignmentVersionUid?: string;
  version?: string;
  note?: string;
  status?: Status;
  url?: string;
  isLatest?: boolean;
  createdAt?: string;
  assignedUsers?: number;
}

export interface AssignmentVersionBodyParams {
  assignmentVersionUid?: string;
  version?: string;
  note?: string;
}

export interface CreateUpdateAssignmentVersionResult {
  assignmentVersionUid?: string;
  /** Legacy backend response casing; read as a fallback until the backend
   * renames its response fields to camelCase. */
  AssignmentVersionUid?: string;
}

export interface AssignmentVersionGridRow {
  assignmentVersionUid: string;
  packageName: string | null;
  version: string;
  note: string | null;
  usersAssigned: number | null;
  creationDate: string | null;
  isLatest?: boolean;
}

export interface AddUpdateAssignmentResult {
  assignmentUid?: string;
  assignmentVersionUid?: string;
}

export interface AssignmentTitleTranslation {
  languageCode: string;
  title: string;
}

export interface AssignmentTitleShortTranslation {
  languageCode: string;
  titleShort: string;
}

export interface AssignmentCreateUpdateBodyParams {
  assignmentUid?: string;
  assignmentType?: AssignmentType;
  packageType?: PackageType;
  status?: Status;
  SKU?: string;
  code?: string;
  title?: string;
  titleShort?: string;
  maxAttempts?: number;
  thumbnail?: string;
  url?: string;
  tags?: string[];
  assignmentVersionUid?: string;
  version?: string;
  note?: string;
  isAvailableToSubportals?: boolean;
  creatorPortalUid?: string;
  assignmentDependency?: AssignmentDependency[];
  pageByPage?: boolean;
  acknowledgement?: boolean;
  mandatory?: boolean;
  titleTranslation?: AssignmentTitleTranslation[];
  titleShortTranslation?: AssignmentTitleShortTranslation[];
}

export interface AssignmentDependency {
  prerequisiteAssignmentUid: string;
  prerequisiteAssignmentId: number;
}

export interface PrerequisiteSummaryResult {
  prerequisiteAssignmentUid: string;
  prerequisiteAssignmentId: number;
  parentAssignmentUid: string;
  level: number;
  userAssignmentUid: string;
  portalAssignmentId: number;
  progressStatus: ProgressStatus;
  title: string;
  queryParams: {
    groupLevel: string;
    parentPortalGroupId?: string;
    parentPortalGroupName?: string;
    portalGroupId?: string;
    portalGroupName?: string;
  };
  portalAssignmentStatus: AssignmentStatus;
  url: string | null;
  isDirectAssignment: boolean;
  dependenciesAfterCount?: number;
  completionPercent?: number;
  progress?: number;
}

export interface CreateUserAssignmentsParams {
  users: string[];
  assignments: string[];
  portalGroupUid: string;
  dueDate?: string;
  plusDays?: number;
  initialNotification: boolean;
}

export interface UpdateAssignment {
  portalAssignmentUid: string;
  users: string[];
}

export interface EditUserAssignmentsParams {
  assignments: UpdateAssignment[];
  portalGroupUid?: string;
  dueDate?: string;
  plusDays?: number;
  emptyDueDate?: boolean;
}

export interface RemoveUserAssignmentsParams {
  assignments: UpdateAssignment[];
  adminUser: string;
}

export interface TranslateBodyParams {
  sourceLanguage: string;
  text: string;
  targetLanguage: string[];
}

export interface TranslateResultItem {
  languageCode: string;
  text: string;
}

export type TranslateResult = TranslateResultItem[];
